import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection } from '../../../components';
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

const RETURN_STYLES = `
  .pr-card {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 2.5rem 2rem;
    box-shadow: 0 4px 24px rgba(15,23,42,0.07);
    text-align: center;
  }
  .pr-icon-wrap {
    width: 72px;
    height: 72px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.25rem;
  }
  .pr-icon-wrap--success { background: #dcfce7; }
  .pr-icon-wrap--failed  { background: #fee2e2; }
  .pr-icon-wrap--pending { background: #fef9c3; }
  .pr-icon-wrap--onsite  { background: #dbeafe; }
  .pr-heading {
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.35rem;
  }
  .pr-amount {
    font-size: 2rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 0.25rem;
  }
  .pr-desc {
    color: #64748b;
    font-size: 0.92rem;
    margin: 0 0 0.5rem;
  }
  .pr-ref {
    font-size: 0.78rem;
    color: #94a3b8;
    margin: 0 0 1.25rem;
  }
  .pr-note {
    font-size: 0.88rem;
    color: #475569;
    margin: 0 0 1.25rem;
  }
  .pr-note--success { color: #16a34a; font-weight: 500; }
  .pr-note--failed  { color: #dc2626; }
  .pr-note--pending { color: #92400e; }
  .pr-next-steps {
    text-align: left;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 1rem 1.1rem;
    margin: 0 0 1.5rem;
  }
  .pr-next-steps-title {
    font-size: 0.82rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0 0 0.65rem;
  }
  .pr-next-steps-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }
  .pr-next-steps-list li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.88rem;
    color: #334155;
  }
  .pr-next-steps-list li svg {
    flex-shrink: 0;
    margin-top: 0.18rem;
    color: #2563eb;
  }
  .pr-onsite-card {
    text-align: left;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 10px;
    padding: 1rem 1.1rem;
    margin: 0 0 1.5rem;
    display: flex;
    gap: 0.6rem;
    color: #1e40af;
    font-size: 0.9rem;
  }
  .pr-onsite-card svg { flex-shrink: 0; margin-top: 0.1rem; }
  .pr-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    justify-content: center;
  }
  .pr-btn {
    border-radius: 8px;
    padding: 0.55rem 1.1rem;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    border: 1px solid transparent;
    transition: background 0.15s;
  }
  .pr-btn--primary { background: #0f172a; color: #fff; border-color: #0f172a; }
  .pr-btn--primary:hover { background: #1e293b; color: #fff; }
  .pr-btn--outline { background: #fff; color: #334155; border-color: #e2e8f0; }
  .pr-btn--outline:hover { background: #f8fafc; color: #0f172a; }
  .pr-btn--danger { background: #dc2626; color: #fff; border-color: #dc2626; }
  .pr-btn--danger:hover { background: #b91c1c; color: #fff; }
  .pr-auto-refresh {
    font-size: 0.78rem;
    color: #94a3b8;
    margin-top: 1rem;
  }
  .pr-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem 0;
    color: #64748b;
    font-size: 0.92rem;
  }
  .pr-spinner {
    width: 36px;
    height: 36px;
    border: 3px solid #e2e8f0;
    border-top-color: #2563eb;
    border-radius: 999px;
    animation: pr-spin 0.8s linear infinite;
  }
  @keyframes pr-spin { to { transform: rotate(360deg); } }
  .pr-pulse {
    animation: pr-pulse 1.5s ease-in-out infinite;
  }
  @keyframes pr-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }
`;

function formatAmount(amount, currency) {
  const c = String(currency || 'EUR').toUpperCase();
  const decimals = c === 'TND' ? 3 : 2;
  return `${Number(amount || 0).toFixed(decimals)} ${c}`;
}

export default function PaymentReturn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const sessionId = params.get('session_id') || '';
    let paymentId = params.get('paymentId') || '';
    if (!paymentId) {
      try {
        paymentId = localStorage.getItem('lastPaymentId') || '';
      } catch {
        paymentId = '';
      }
    }

    const fetchOnce = async () => {
      try {
        if (sessionId) {
          const res = await api.getPaymentByStripeSession(sessionId);
          if (res?.success) {
            setPayment(res.data);
            if (['completed', 'failed', 'expired', 'on_site'].includes(res.data.status)) {
              if (pollRef.current) clearInterval(pollRef.current);
            }
            return;
          }
        }
        if (paymentId) {
          const res = await api.getPayment(paymentId);
          if (res?.success) {
            setPayment(res.data);
            if (['completed', 'failed', 'expired', 'on_site'].includes(res.data.status)) {
              if (pollRef.current) clearInterval(pollRef.current);
            }
          }
        }
      } catch (e) {
        setError(e.message || t('paymentReturn.verifyError'));
      } finally {
        setLoading(false);
      }
    };

    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [location.search, t]);

  const status = payment?.status || 'pending';
  const paymentRef = payment?.stripeSessionId || payment?.stripePaymentIntentId || payment?._id || '-';

  const renderStatusIcon = () => {
    if (status === 'completed') {
      return (
        <div className="pr-icon-wrap pr-icon-wrap--success">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      );
    }
    if (status === 'failed' || status === 'expired') {
      return (
        <div className="pr-icon-wrap pr-icon-wrap--failed">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </div>
      );
    }
    if (status === 'on_site') {
      return (
        <div className="pr-icon-wrap pr-icon-wrap--onsite">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="2" y="5" width="20" height="14" rx="2" stroke="#2563eb" strokeWidth="1.5" />
            <path d="M2 10h20" stroke="#2563eb" strokeWidth="1.5" />
          </svg>
        </div>
      );
    }
    return (
      <div className="pr-icon-wrap pr-icon-wrap--pending">
        <div className="pr-spinner" aria-label={t('paymentReturn.verifying')} />
      </div>
    );
  };

  const renderHeading = () => {
    if (status === 'completed') return <h2 className="pr-heading">{t('paymentReturn.successHeading')}</h2>;
    if (status === 'failed' || status === 'expired') return <h2 className="pr-heading">{t('paymentReturn.failedHeading')}</h2>;
    if (status === 'on_site') return <h2 className="pr-heading">{t('paymentReturn.onSiteRegistered')}</h2>;
    return <h2 className="pr-heading pr-pulse">{t('paymentReturn.pendingHeading')}</h2>;
  };

  const renderNote = () => {
    if (status === 'completed') return <p className="pr-note pr-note--success">{t('paymentReturn.successNote')}</p>;
    if (status === 'failed' || status === 'expired') return <p className="pr-note pr-note--failed">{t('paymentReturn.failedNote')}</p>;
    if (status === 'on_site') return null;
    return <p className="pr-note pr-note--pending">{t('paymentReturn.pendingNote')}</p>;
  };

  const renderNextSteps = () => {
    if (status !== 'completed') return null;
    return (
      <div className="pr-next-steps">
        <p className="pr-next-steps-title">{t('paymentReturn.nextStepsTitle')}</p>
        <ul className="pr-next-steps-list">
          {[t('paymentReturn.next1'), t('paymentReturn.next2'), t('paymentReturn.next3')].map((step, i) => (
            <li key={i}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>{step}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderOnSiteCard = () => {
    if (status !== 'on_site') return null;
    return (
      <div className="pr-onsite-card">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 8v4m0 4h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span>{t('paymentReturn.onSiteNote')}</span>
      </div>
    );
  };

  const renderActions = () => (
    <div className="pr-actions">
      <a href="/" className="pr-btn pr-btn--outline">{t('paymentReturn.backToHome')}</a>
      {(status === 'failed' || status === 'expired') && (
        <button className="pr-btn pr-btn--danger" onClick={() => navigate('/payment')}>
          {t('paymentReturn.retryPayment')}
        </button>
      )}
      {(status === 'failed' || status === 'expired') && (
        <a href="/contact-us" className="pr-btn pr-btn--outline">{t('paymentReturn.contactSupport')}</a>
      )}
      <a href="/profile" className="pr-btn pr-btn--primary">{t('paymentReturn.goToProfile')}</a>
    </div>
  );

  return (
    <AppLayout title={t('paymentReturn.pageTitle')} rootClass="layout-1">
      <style>{RETURN_STYLES}</style>
      <NioSection className="pt-120 pt-lg-160 pb-5">
        <Row className="justify-content-center">
          <Col lg={7} xl={5}>
            <div className="pr-card">
              {loading && !payment ? (
                <div className="pr-loading">
                  <div className="pr-spinner" />
                  <span>{t('paymentReturn.verifying')}</span>
                </div>
              ) : (
                <>
                  {renderStatusIcon()}
                  {renderHeading()}

                  {payment && (
                    <>
                      <p className="pr-amount">{formatAmount(payment.amount, payment.currency)}</p>
                      {payment.description && <p className="pr-desc">{payment.description}</p>}
                      <p className="pr-ref">{t('common.reference')} : <code>{paymentRef}</code></p>
                    </>
                  )}

                  {renderNote()}
                  {renderOnSiteCard()}
                  {renderNextSteps()}

                  {error && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: '8px', padding: '0.6rem 0.8rem', fontSize: '0.88rem', marginBottom: '1rem' }}>
                      {error}
                    </div>
                  )}

                  {renderActions()}

                  {status === 'pending' && (
                    <p className="pr-auto-refresh">{t('paymentReturn.autoRefresh')}</p>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </NioSection>
    </AppLayout>
  );
}
